import React, { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import CelebrationCard from "@/components/celebrations/CelebrationCard";
import LightPop from "@/components/celebrations/LightPop";

const DURATIONS = { confetti: 2800, star: 2800, fireworks: 3800, slide_card: 3400, thumbs_up: 2100, checkmark: 2100 };
const LIGHT = new Set(["thumbs_up", "checkmark"]);
let localSeq = 0;

// Plays celebrations one at a time: server ones (team members' notifications, polled + acked) and local ones
// fired via `celebrate()` (any role). Off when Animations are off; members also follow the coach's team toggle.
export default function CelebrationOverlay({ enabled = true, ready = false }) {
  const { user, impersonating, viewOnly } = useAuth();
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const [teamOn, setTeamOn] = useState(true);
  const seenRef = useRef(new Set());
  const isMember = user?.role === "team_member";
  const on = enabled && (!isMember || teamOn);
  const onRef = useRef(on);
  onRef.current = on;

  const push = useCallback((items) => setQueue((q) => [...q, ...items]), []);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/member/celebrations");
      setTeamOn(data.enabled !== false);
      const fresh = (data.items || []).filter((c) => !seenRef.current.has(c.id));
      fresh.forEach((c) => seenRef.current.add(c.id));
      if (fresh.length) push(fresh);
    } catch { /* ignore */ }
  }, [push]);

  useEffect(() => {
    if (!isMember || !enabled) return undefined;
    poll();
    const t = setInterval(poll, 15000);
    return () => clearInterval(t);
  }, [isMember, enabled, poll]);

  useEffect(() => {
    const onLocal = (e) => {
      if (!onRef.current) return;
      const { type, title, message } = e.detail || {};
      push([{ id: `local-${++localSeq}`, local: true, celebration: type, title, message }]);
    };
    window.addEventListener("rpg:celebrate", onLocal);
    return () => window.removeEventListener("rpg:celebrate", onLocal);
  }, [push]);

  // First-ever login: fireworks once per new account (existing accounts were marked welcomed).
  useEffect(() => {
    if (!ready || !user || impersonating || viewOnly || user.welcome_celebrated) return;
    api.post("/auth/welcome-celebrated").catch(() => {});
    if (on) push([{ id: "local-welcome", local: true, celebration: "fireworks", title: `Welcome to RallyPointGo, ${user.first_name || "friend"}!`, message: "Your account is all set. Let's get started." }]);
  }, [ready, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (current || !queue.length) return;
    const [next, ...rest] = queue;
    setCurrent(next);
    setQueue(rest);
  }, [queue, current]);

  useEffect(() => {
    if (!current) return undefined;
    if (!current.local) api.post("/member/celebrations/ack", { ids: [current.id] }).catch(() => {});
    const t = setTimeout(() => setCurrent(null), DURATIONS[current.celebration] || 2800);
    return () => clearTimeout(t);
  }, [current]);

  if (!on || !current) return null;
  return LIGHT.has(current.celebration) ? <LightPop key={current.id} item={current} /> : <CelebrationCard key={current.id} item={current} onClose={() => setCurrent(null)} />;
}
